/**
 * My Financial Assistant (TMMAPP-0142 U1; MM-D-146): `/me/assistant` and `/me/assistant/{id}`. A
 * workspace like Claude Code's, with financial scope in place of repositories: sessions · the chat
 * (· the panel). In v2 (TMMAPP-0153, visual contract v2 §11 and §15) it fills the page below the top
 * bar, edge to edge, on `bg`; the page's `h1` is visually hidden (the sessions list's title shows).
 *
 * - **xl:** three columns: the sessions list (250 px, a `line` rule), the chat, and the panel
 *   (privacy, or Help & feedback's help and tickets). **lg:** the list and the chat; the panel is a
 *   right drawer opened from the chat header's button. **Below lg:** the list or the chat, with Back
 *   (and the panel in the chat's place); choosing a session, Back, and opening or closing the panel
 *   move focus to the new pane's heading (H5).
 * - Each pane is a named landmark (a `section` labelled by its heading).
 * - **New conversation** opens a menu: Private or Shared, each the create dialog on that kind. Both
 *   follow `useSubscriberAction()`: a person without a subscription sees the page and the button,
 *   and choosing one opens the purchase experience.
 * - The session and its privacy panel are read whole when it opens. After each turn the page reads
 *   only that turn's privacy calls (`?turnId=`) and merges them in (H3), and re-reads the session
 *   (usage) and the wallet. An answer for a session the person has left is dropped (H2).
 * - **Shared sessions** (U3): the members (the session view's `members[]`, J3) show as avatars;
 *   each member adds and removes their own data (the session's banner); archive and delete are for
 *   whoever may manage it, and confirm "for everyone".
 * - **A guest** (GUEST or NONE tier, M11) reaches the page from the nav: the list is what the
 *   server lists for them (Help & feedback and the shared sessions they were invited to). New
 *   session and Add my accounts open the purchase experience; in a shared session they ask when the
 *   owner allowed it, on the owner's credit (TMMAPP-0164 item 14).
 * - **Help & feedback** (U2): `POST …/sessions/support/ensure` once per visit; the session is pinned
 *   first in the list (left out on `404 PERSON_NOT_FOUND`), and its right panel is the person's help
 *   and tickets (`SupportPanel`), with "What the assistant saw" for its AI disclosure. A ticket link
 *   (`?ticket={number}`, from the ticket emails' `/assistant` redirect) opens Help & feedback on
 *   that ticket; when Help & feedback can't be opened, the link says it couldn't be opened (M7).
 *   An answer's article links open only in Help & feedback (M5). Its ticket drafts are the
 *   transcript's (TMMAPP-0142 L8, replacing U6's page-held drafts); below `md` the chat stays
 *   mounted, hidden, while Help and tickets show. The sidebar's Help & feedback (`?help=1`,
 *   TMMAPP-0150 U2) opens that session.
 * - **AI tools** (U7, L2): every session but Help & feedback opens its connected AI tools from the
 *   chat's header (`SessionAgentToolsDialog`); a change re-reads the session (its `agentGrants`).
 *   The privacy panel's agent calls (`agentCalls`) are the whole log's: a turn's read keeps them.
 * - A session that disappears (404) shows the "no longer available" copy (K7). A turn's privacy
 *   calls merge into a log that was read; with no log yet, the whole log is read (K8).
 * - While the page is open, a file dropped outside the composer never opens in the browser (K5).
 * - **Locked** (`me.locked`; Operator, 2026-10-16, M1): Help & feedback's tickets only
 *   (`LockedAssistant`), with no session, chat, help or announcement read.
 * - **Breakouts** (TMMAPP-0148 U1; v2's breakout, TMMAPP-0153 items 2, 5, 9; the room tabs,
 *   TMMAPP-0178 items 1–6, from the Operator's mock-up, which supersede 0153's Operator decision 3):
 *   the chat's **Private room** tab opens `myBreakoutId`, or makes it (`POST …/breakout`, once
 *   however often it's pressed) and then opens it; the room's **Shared room** tab goes back, with no
 *   prompt. The list holds top-level conversations only: a room has no row while its shared session
 *   reads (that row is the current one while in it), and its Delete is in its header's menu; one
 *   whose shared session is gone keeps its own row ("Private room · {name}"). In the room, **Bring
 *   something back** opens **Bring something back?**: Cancel stays; Share & return posts the note
 *   as the person's own message (`askAi: false`), then goes there. The page reads the
 *   shared session's view for the room's callout (who can't see it) and to know whether it still
 *   takes messages. A GUEST or NONE tier is never offered one and isn't shown one (item 6a).
 *   Nothing of another member's room is ever read or shown: the server serves none.
 * - **Search across sessions** (TMMAPP-0148 U2 item 13): the sessions pane's field searches every
 *   session the person lists (`GET /me/assistant/search`), grouped by session; a private room's hits
 *   are "My private room · {shared session}" (the lists, rooms included, say which sessions are
 *   rooms), and a GUEST or NONE tier is shown hits only in the sessions listed for them, never a
 *   room's (item 6a). A hit opens `/me/assistant/{id}?message={lineId}`: the chat opens on the
 *   window around that line, and the parameter is then dropped (replace).
 * - Nothing here is stored in the browser.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Box, Button, Drawer, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { aiWalletApi, type AiWallet } from '@/api/aiWallet';
import {
  assistantApi,
  type AssistantMessage,
  type AssistantOptions,
  type AssistantPrivacy,
  type AssistantPrivateData,
  type AssistantScopeEntity,
  type AssistantScopeRequest,
  type AssistantSearchHit,
  type AssistantSession,
} from '@/api/assistant';
import { errorCopy } from '@/api/errorCopy';
import { isApiError } from '@/api/errors';
import { parseTicketNumber } from '@/api/tickets';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { CloseIcon, HelpIcon, PreviousIcon, PrivacyIcon } from '@/components/icons';
import { personName } from '@/i18n/personName';
import { useMe } from '@/session/meHooks';
import { useSubscriberAction } from '@/session/useSubscriberAction';
import { useNotify } from '@/shell/NotifyProvider';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { resolveTokens } from '@/theme/theme';
import { privateDataItem } from './assistantCopy';
import { ChatPanel, type RoomParent } from './ChatPanel';
import { LockedAssistant } from './LockedAssistant';
import { PrivacyPanel } from './PrivacyPanel';
import { privateDataError, withoutItem } from './privateData';
import { PrivateDataDialog } from './PrivateDataDialog';
import { RenameSessionDialog } from './RenameSessionDialog';
import { grantableScope } from './scopeGrant';
import { SessionList, type SearchPlace, type SessionAction } from './SessionList';
import { SessionScopeDialog } from './SessionScopeDialog';
import { askedDraftOf } from './askAbout';
import { otherMemberNames } from './sessionKind';
import { SupportPanel, type SupportRequest } from './support/SupportPanel';
import { LazyRootContext } from '@/shared/lazyRootContext';
import { SessionAgentToolsDialog } from '@/features/agentTools/SessionAgentToolsDialog';

type DialogState =
  | { kind: 'create'; visibility: 'PRIVATE' | 'SHARED' }
  | {
      kind: 'scope';
      session: AssistantSession;
      /** A private room's shared session: its name and categories. */
      parent?: { name: string; categories: Record<string, boolean> } | undefined;
    }
  | { kind: 'rename'; session: AssistantSession }
  | { kind: 'archive'; session: AssistantSession }
  | { kind: 'delete'; session: AssistantSession }
  | { kind: 'privateData'; session: AssistantSession }
  | { kind: 'removePrivate'; session: AssistantSession; item: AssistantPrivateData }
  | { kind: 'agentTools'; session: AssistantSession }
  | null;

type Pane = 'sessions' | 'chat' | 'privacy';

const PRIVACY_WIDTH = 320;
/** Help & feedback's panel holds tabs and tickets: a little wider. */
const SUPPORT_WIDTH = 360;
/** The sessions list's column (visual contract v2 §11). */
const LIST_WIDTH = 250;

/** A file dragged over the page outside the composer: never let the browser open it (K5). */
function guardFileDrop(event: DragEvent) {
  if (event.dataTransfer?.types.includes('Files')) event.preventDefault();
}

function parseId(raw: string | undefined): number | null {
  if (raw === undefined || !/^\d+$/.test(raw)) return null;
  return Number(raw);
}

/**
 * The privacy panel with one turn's calls merged in (H3): that turn's calls replace its old ones.
 * A turn's read serves no agent calls (they belong to no turn), so the log's are kept.
 */
function mergePrivacy(current: AssistantPrivacy | null, turn: AssistantPrivacy, turnId: number) {
  if (current === null) return turn;
  const others = current.sent.filter((call) => call.turnId !== turnId);
  const sent = [...others, ...turn.sent.filter((call) => call.turnId === turnId)].sort(
    (a, b) => a.aiRequestId - b.aiRequestId,
  );
  return { ...turn, sent, agentCalls: current.agentCalls };
}

/**
 * The rows the lists hold: every session but Help & feedback (pinned on its own). A breakout is the
 * person's own (no API serves another's), reached from its shared session's tabs (the list shows no
 * row for it while that reads, TMMAPP-0178 item 4); a GUEST or NONE tier isn't shown one while its
 * shared session reads (0148 item 6a).
 */
function listed(row: AssistantSession, subscriber: boolean): boolean {
  if (row.kind === 'SUPPORT') return false;
  return subscriber || !(row.visibility === 'BREAKOUT' && row.parentAvailable);
}

/** What a breakout needs of its shared session: its other members, its Share, and asking there. */
function roomParentOf(parent: AssistantSession, others: string[]): RoomParent {
  const mine = parent.members.find((member) => member.mine) ?? null;
  const owner = parent.members.find((member) => member.owner && !member.mine) ?? null;
  return {
    others,
    members: parent.members,
    writable: parent.status === 'ACTIVE' && !parent.accessEnded,
    canAsk: mine?.canAsk ?? true,
    askReason: mine?.canAskReason ?? null,
    ownerName:
      parent.paidBy !== null && !parent.paidBy.mine ? parent.paidBy.name : (owner?.name ?? null),
  };
}

/** A locked member reaches Help & feedback's tickets only (M1); everyone else, the workspace. */
export function AssistantPage() {
  const me = useMe();
  return me.locked ? <LockedAssistant /> : <AssistantWorkspace />;
}

function AssistantWorkspace() {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const notify = useNotify();
  const navigate = useNavigate();
  const me = useMe();
  const title = usePageTitle({ title: t('nav.assistant') });
  const xl = useMediaQuery(theme.breakpoints.up('xl'));
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const sessionId = parseId(useParams().sessionId);
  const [searchParams, setSearchParams] = useSearchParams();
  const ticketParam = parseTicketNumber(searchParams.get('ticket'));
  const messageParam = parseId(searchParams.get('message') ?? undefined);
  // The sidebar's Help & feedback (TMMAPP-0150 U2): `?help=1` opens the Help & feedback session.
  const helpParam = searchParams.get('help') !== null;
  const sessionsHeading = useId();
  const chatHeading = useId();
  const privacyHeading = useId();

  const [options, setOptions] = useState<AssistantOptions | null>(null);
  const [active, setActive] = useState<AssistantSession[] | null>(null);
  const [archived, setArchived] = useState<AssistantSession[] | null>(null);
  // Every session listed, private rooms included: what a cross-session search hit is in.
  const [known, setKnown] = useState<ReadonlyMap<number, AssistantSession>>(new Map());
  // A search hit's line to open (`?message=`), for that session's chat.
  const [jump, setJump] = useState<{ sessionId: number; messageId: number; nonce: number } | null>(
    null,
  );
  const jumpNonce = useRef(0);
  const [listError, setListError] = useState<unknown>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [session, setSession] = useState<AssistantSession | null>(null);
  const [sessionError, setSessionError] = useState<unknown>(null);
  const [privacy, setPrivacy] = useState<AssistantPrivacy | null>(null);
  const [privacyError, setPrivacyError] = useState<unknown>(null);
  const [wallet, setWallet] = useState<AiWallet | null>(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  // Help & feedback: the person's support session (null until ensured, or when there is none).
  const [support, setSupport] = useState<AssistantSession | null>(null);
  const [supportPrivacy, setSupportPrivacy] = useState(false);
  const [supportRequest, setSupportRequest] = useState<SupportRequest | null>(null);
  const [ticketsVersion, setTicketsVersion] = useState(0);
  // `ensure` failed: there is no Help & feedback to open a ticket link in (M7).
  const [supportFailed, setSupportFailed] = useState(false);
  const requestNonce = useRef(0);
  // The privacy pane's scroll area: the help browser's pictures load as it scrolls (M9).
  const [sideScroll, setSideScroll] = useState<HTMLElement | null>(null);
  // Ask about this (TMMAPP-0151 §B): the page it was asked from navigates here with the suggested
  // question in the route state, and the composer starts with it; nothing is sent until the person
  // sends it.
  const location = useLocation();
  const [composerDraft, setComposerDraft] = useState<{ sessionId: number; text: string } | null>(
    () => {
      const draft = askedDraftOf(location.state);
      return draft !== null && sessionId !== null ? { sessionId, text: draft } : null;
    },
  );
  // The route state is spent once read: a reload or Back doesn't fill the composer again.
  const askedDraft = askedDraftOf(location.state) !== null;
  useEffect(() => {
    if (!askedDraft) return;
    void navigate({ pathname: location.pathname, search: location.search }, { replace: true });
  }, [askedDraft, navigate, location.pathname, location.search]);
  // Opening the private room (made the first time): one request however often it's pressed.
  const [openingRoom, setOpeningRoom] = useState(false);
  const roomOpening = useRef(false);
  // A breakout's shared session as read (its members, and whether it takes messages), with the
  // parent id and the room's `parentAvailable` it was read for (TMMAPP-0166 item 1). `gone`: the read
  // answered 404 (MM-D-05); any other failure is failed, not gone, and kept as `error` for Retry
  // (TMMAPP-0167 item 1). `attempt` is the retry the read answered.
  const [parentRead, setParentRead] = useState<{
    id: number;
    available: boolean;
    attempt: number;
    view: AssistantSession | null;
    gone: boolean;
    error: unknown;
  } | null>(null);
  // Bumped by the failed parent read's Retry: the effect reads the shared session again.
  const [parentAttempt, setParentAttempt] = useState(0);

  // The session on screen: an answer for another one is dropped (H2).
  const currentId = useRef<number | null>(sessionId);
  useEffect(() => {
    currentId.current = sessionId;
  }, [sessionId]);

  const subscriber = me.tier === 'SUBSCRIBER';
  const loadLists = useCallback(async () => {
    try {
      const [nextActive, nextArchived] = await Promise.all([
        assistantApi.list('ACTIVE'),
        assistantApi.list('ARCHIVED'),
      ]);
      // Help & feedback is pinned on its own, never listed among the sessions.
      setActive(nextActive.filter((row) => listed(row, subscriber)));
      setArchived(nextArchived.filter((row) => listed(row, subscriber)));
      setKnown(new Map([...nextActive, ...nextArchived].map((row) => [row.id, row])));
      setListError(null);
    } catch (error) {
      setListError(error);
    }
  }, [subscriber]);

  const loadSession = useCallback(async (id: number) => {
    try {
      const next = await assistantApi.get(id);
      if (currentId.current !== id) return;
      setSession(next);
      setSessionError(null);
    } catch (error) {
      if (currentId.current !== id) return;
      // Gone (deleted under an open page): the chat gives way to "no longer available" (K7).
      if (isApiError(error) && error.status === 404) setSession(null);
      setSessionError(error);
    }
  }, []);

  // What's held of the privacy log, to tell which arriving answers have no calls yet (C3).
  const privacyRef = useRef<AssistantPrivacy | null>(null);
  useEffect(() => {
    privacyRef.current = privacy;
  }, [privacy]);

  /**
   * The whole panel (on open), or one turn's calls merged in (after a turn). With no log held yet
   * (the read on open failed), a turn's read is the whole log, never one turn's calls alone (K8).
   */
  const loadPrivacy = useCallback(async (id: number, turnId?: number) => {
    const whole = turnId === undefined || privacyRef.current === null;
    try {
      const next = await assistantApi.privacy(id, whole ? undefined : turnId);
      if (currentId.current !== id) return;
      setPrivacy((current) =>
        whole || turnId === undefined ? next : mergePrivacy(current, next, turnId),
      );
      setPrivacyError(null);
    } catch (error) {
      if (currentId.current === id) setPrivacyError(error);
    }
  }, []);

  const loadWallet = useCallback(async () => {
    try {
      setWallet(await aiWalletApi.get());
    } catch {
      /* no balance shown without a wallet */
    }
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      setOptions(await assistantApi.options());
    } catch {
      setOptions(null);
    }
  }, []);

  useEffect(() => {
    queueMicrotask(() => {
      void loadLists();
      void loadOptions();
      void loadWallet();
    });
  }, [loadLists, loadOptions, loadWallet]);

  // Help & feedback, once per visit: made on the first call, found on every later one. Without it
  // (`404 PERSON_NOT_FOUND`, or any failure) the row is left out and the rest of the page works.
  useEffect(() => {
    let live = true;
    assistantApi.ensureSupport().then(
      (made) => {
        if (live) setSupport(made);
      },
      () => {
        // No Help & feedback row; a ticket link says it couldn't be opened.
        if (live) setSupportFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, []);

  // A file dropped just outside the composer never opens in the browser (K5).
  useEffect(() => {
    window.addEventListener('dragover', guardFileDrop);
    window.addEventListener('drop', guardFileDrop);
    return () => {
      window.removeEventListener('dragover', guardFileDrop);
      window.removeEventListener('drop', guardFileDrop);
    };
  }, []);

  // A session's detail and privacy, re-read when the route changes.
  useEffect(() => {
    let live = true;
    queueMicrotask(() => {
      if (!live) return;
      setSession(null);
      setSessionError(null);
      setPrivacy(null);
      setPrivacyError(null);
      setPrivacyOpen(false);
      setSupportPrivacy(false);
      if (sessionId === null) return;
      void loadSession(sessionId);
      void loadPrivacy(sessionId);
    });
    return () => {
      live = false;
    };
  }, [sessionId, loadSession, loadPrivacy]);

  // A breakout's shared session, read for its callout and its Share (TMMAPP-0153 items 6, 9), and
  // (TMMAPP-0164 item 14) while the room reads as unavailable: a shared session that still reads, where
  // the person can't ask, makes the room read-only with the reason rather than gone.
  const parentId =
    session !== null && session.id === sessionId && session.visibility === 'BREAKOUT'
      ? session.parentSessionId
      : null;
  // Re-read when the room's `parentAvailable` changes too: a room that goes from writable to
  // can't-ask needs the parent's current `canAsk`, not the one read at first open (TMMAPP-0166 item 1).
  const parentAvailable = session !== null && session.parentAvailable;
  useEffect(() => {
    if (parentId === null) return undefined;
    let live = true;
    const read = { id: parentId, available: parentAvailable, attempt: parentAttempt };
    assistantApi.get(parentId).then(
      (view) => {
        if (live) setParentRead({ ...read, view, gone: false, error: null });
      },
      (error: unknown) => {
        // Unread: the callout names no one, and Share is offered (the server still decides).
        const gone = isApiError(error) && error.status === 404;
        if (live) setParentRead({ ...read, view: null, gone, error: gone ? null : error });
      },
    );
    return () => {
      live = false;
    };
  }, [parentId, parentAvailable, parentAttempt]);
  // A read for another parent is never used; one for this parent settles only once it was read for
  // the room's current `parentAvailable`, and either read or answered 404. A failed read (a 5xx, the
  // network) never settles: a paused room is never offered Delete for it, only the failure and Retry
  // (TMMAPP-0167 item 1), until the retry's own read answers.
  const parentView = parentRead !== null && parentRead.id === parentId ? parentRead.view : null;
  const parentCurrent =
    parentRead !== null &&
    parentRead.id === parentId &&
    parentRead.available === parentAvailable &&
    parentRead.attempt === parentAttempt;
  const parentSettled =
    parentId === null || (parentCurrent && (parentRead.view !== null || parentRead.gone));
  const parentFailure =
    parentId !== null && parentCurrent && parentRead.error !== null
      ? { error: parentRead.error, onRetry: () => setParentAttempt((n) => n + 1) }
      : null;

  const dropParam = useCallback(
    (name: 'ticket' | 'message' | 'help') =>
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(name);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const dropTicketParam = useCallback(() => dropParam('ticket'), [dropParam]);

  // A search hit's link (`?message=`): the chat opens around that line; the parameter is dropped.
  useEffect(() => {
    if (messageParam === null || sessionId === null) return;
    queueMicrotask(() => {
      jumpNonce.current += 1;
      setJump({ sessionId, messageId: messageParam, nonce: jumpNonce.current });
      dropParam('message');
    });
  }, [messageParam, sessionId, dropParam]);
  // Another session: a line found in an earlier one is never opened again.
  useEffect(() => {
    if (jump === null || jump.sessionId === sessionId) return;
    queueMicrotask(() => setJump(null));
  }, [jump, sessionId]);

  // A ticket link with no Help & feedback to open it in says so, never silently (M7).
  useEffect(() => {
    if (ticketParam === null || !supportFailed) return;
    queueMicrotask(() => {
      notify(t('support.ticketLinkFailed'), { severity: 'error' });
      dropTicketParam();
    });
  }, [ticketParam, supportFailed, notify, t, dropTicketParam]);

  // A ticket link (`?ticket=`) opens Help & feedback on that ticket; the parameter is then dropped.
  const supportId = support?.id ?? null;
  useEffect(() => {
    if (ticketParam === null || supportId === null) return;
    if (sessionId !== supportId) {
      void navigate(`/me/assistant/${supportId}?ticket=${ticketParam}`, { replace: true });
      return;
    }
    queueMicrotask(() => {
      requestNonce.current += 1;
      setSupportRequest({ kind: 'ticket', number: ticketParam, nonce: requestNonce.current });
      setPrivacyOpen(true);
      dropTicketParam();
    });
  }, [ticketParam, supportId, sessionId, navigate, dropTicketParam]);

  // The sidebar's Help & feedback (`?help=1`): the Help & feedback session once it's ensured. Without
  // one, the page stays as it is and the parameter is dropped.
  useEffect(() => {
    if (!helpParam) return;
    if (supportId !== null) {
      void navigate(`/me/assistant/${supportId}`, { replace: true });
      return;
    }
    if (supportFailed) queueMicrotask(() => dropParam('help'));
  }, [helpParam, supportId, supportFailed, navigate, dropParam]);

  /** Opens something in Help & feedback's panel (an AI answer's article, a filed ticket). */
  const openInSupport = useCallback(
    (request: { kind: 'article'; id: string } | { kind: 'ticket'; number: number }) => {
      requestNonce.current += 1;
      setSupportRequest({ ...request, nonce: requestNonce.current });
      setSupportPrivacy(false);
      setPrivacyOpen(true);
    },
    [],
  );

  /**
   * Messages arrived through the live transcript (C3). An answer to another member's question
   * reads that turn's privacy calls (its cost, and the shared panel) unless they're held; the
   * caller's own turns are read when they end (`onTurnEnd`). Another member's message or answer
   * re-reads the session: usage, the banner, archived.
   */
  const onArrived = useCallback(
    (arrived: readonly AssistantMessage[], all: readonly AssistantMessage[]) => {
      if (sessionId === null) return;
      const held = new Set((privacyRef.current?.sent ?? []).map((call) => call.turnId));
      const mineAsked = new Set(
        all.flatMap((message) =>
          message.role === 'USER' && message.mine && message.turnId !== null
            ? [message.turnId]
            : [],
        ),
      );
      const others = new Set(
        arrived.flatMap((message) =>
          message.role === 'ASSISTANT' && message.turnId !== null && !mineAsked.has(message.turnId)
            ? [message.turnId]
            : [],
        ),
      );
      for (const turnId of others) {
        if (!held.has(turnId)) void loadPrivacy(sessionId, turnId);
      }
      const fromOthers = arrived.some((message) => message.role === 'USER' && !message.mine);
      if (others.size > 0 || fromOthers) void loadSession(sessionId);
    },
    [sessionId, loadPrivacy, loadSession],
  );

  const onTurnEnd = useCallback(
    (turnId: number) => {
      if (sessionId === null) return;
      void loadSession(sessionId);
      void loadPrivacy(sessionId, turnId);
      void loadWallet();
      void loadLists();
    },
    [sessionId, loadSession, loadPrivacy, loadWallet, loadLists],
  );

  const onAllowScope = useCallback(
    async (request: AssistantScopeRequest) => {
      if (session === null) throw new Error('no session');
      // Only what this person may add here (never a 422): see `grantableScope`.
      const grant = grantableScope(session, options, request);
      const scope: AssistantScopeEntity[] = session.scope
        .filter((item) => item.available)
        .map(({ kind, id }) => ({ kind, id }));
      for (const entity of grant.entities) {
        if (!scope.some((item) => item.kind === entity.kind && item.id === entity.id)) {
          scope.push(entity);
        }
      }
      const categories = { ...session.categories };
      for (const key of grant.categories) categories[key] = true;
      const saved = await assistantApi.patch(session.id, {
        ...(grant.entities.length > 0 ? { scope } : {}),
        ...(grant.categories.length > 0 ? { categories } : {}),
      });
      setSession(saved);
      void loadPrivacy(saved.id);
    },
    [session, options, loadPrivacy],
  );

  /** The list's unread count drops to 0 for the session read. */
  const onRead = useCallback((id: number) => {
    const clear = (list: AssistantSession[] | null) =>
      list?.map((row) =>
        row.id === id && row.unreadCount > 0 ? { ...row, unreadCount: 0 } : row,
      ) ?? null;
    setActive(clear);
    setArchived(clear);
  }, []);

  /** "My private room": its route, made first when the person has none yet (idempotent). */
  async function openRoom(shared: AssistantSession) {
    if (shared.myBreakoutId !== null) {
      void navigate(`/me/assistant/${shared.myBreakoutId}`);
      return;
    }
    if (roomOpening.current) return;
    roomOpening.current = true;
    setOpeningRoom(true);
    try {
      const opened = await assistantApi.openBreakout(shared.id);
      void navigate(`/me/assistant/${opened.id}`);
      // The lists know the new room (search places its hits; TMMAPP-0178 item 5).
      void loadLists();
    } catch (error) {
      notify(errorCopy(t, error, 'assistant.breakout.openError'), { severity: 'error' });
    } finally {
      roomOpening.current = false;
      setOpeningRoom(false);
    }
  }

  /** The room's Shared room tab: its shared session, with no prompt (TMMAPP-0178 item 3). */
  function openShared(room: AssistantSession) {
    if (room.parentSessionId === null) return;
    void navigate(`/me/assistant/${room.parentSessionId}`);
  }

  /**
   * Share & return (item 9): the note, as the person's own message to the shared session (a message
   * to its members, `askAi: false`, free), then the shared session. A refusal is the dialog's.
   */
  async function shareBack(room: AssistantSession, note: string) {
    if (room.parentSessionId === null) return;
    await assistantApi.send(room.parentSessionId, note, { tier: 'FAST', askAi: false });
    void navigate(`/me/assistant/${room.parentSessionId}`);
    void loadLists();
  }

  /** Edit scope; a private room's reads its shared session's categories first (item 3). */
  async function editScope(target: AssistantSession) {
    if (target.visibility !== 'BREAKOUT' || target.parentSessionId === null) {
      openDialog({ kind: 'scope', session: target });
      return;
    }
    try {
      const parent = await assistantApi.get(target.parentSessionId);
      openDialog({
        kind: 'scope',
        session: target,
        parent: { name: parent.name, categories: parent.categories },
      });
    } catch (error) {
      notify(errorCopy(t, error, 'assistant.breakout.parentLoadError'), { severity: 'error' });
    }
  }

  // The shared session's composer has taken the draft: it isn't offered again.
  const draftTaken = composerDraft !== null && session?.id === composerDraft.sessionId;
  useEffect(() => {
    if (!draftTaken) return;
    queueMicrotask(() => setComposerDraft(null));
  }, [draftTaken]);

  const newPrivate = useSubscriberAction(() =>
    setDialog({ kind: 'create', visibility: 'PRIVATE' }),
  );
  const newShared = useSubscriberAction(() => setDialog({ kind: 'create', visibility: 'SHARED' }));
  // "Add my accounts": a guest's opens the purchase experience (M11).
  const addMine = useSubscriberAction(() => {
    if (session !== null) openDialog({ kind: 'privateData', session });
  });

  function openDialog(next: DialogState) {
    setDialogError(null);
    setDialog(next);
  }

  async function archiveOrRestore(target: AssistantSession, action: 'archive' | 'restore') {
    try {
      const saved =
        action === 'archive'
          ? await assistantApi.archive(target.id)
          : await assistantApi.restore(target.id);
      if (saved.id === sessionId) setSession(saved);
      notify(t(action === 'archive' ? 'assistant.archivedToast' : 'assistant.restoredToast'));
      await loadLists();
      return true;
    } catch (error) {
      const message = errorCopy(t, error, 'assistant.error.generic');
      if (dialog !== null) setDialogError(message);
      else notify(message, { severity: 'error' });
      return false;
    }
  }

  async function act(target: AssistantSession, action: SessionAction) {
    if (action === 'rename') {
      openDialog({ kind: 'rename', session: target });
      return;
    }
    if (action === 'delete') {
      openDialog({ kind: 'delete', session: target });
      return;
    }
    // Archiving a shared session confirms first: it is read-only for every member.
    if (action === 'archive' && target.visibility === 'SHARED') {
      openDialog({ kind: 'archive', session: target });
      return;
    }
    await archiveOrRestore(target, action);
  }

  async function confirmArchive(target: AssistantSession) {
    setDialogBusy(true);
    setDialogError(null);
    if (await archiveOrRestore(target, 'archive')) setDialog(null);
    setDialogBusy(false);
  }

  async function confirmDelete(target: AssistantSession) {
    setDialogBusy(true);
    setDialogError(null);
    try {
      await assistantApi.remove(target.id);
      setDialog(null);
      notify(t('assistant.deletedToast'));
      if (target.id === sessionId) void navigate('/me/assistant');
      await loadLists();
    } catch (error) {
      setDialogError(errorCopy(t, error, 'assistant.error.generic'));
    } finally {
      setDialogBusy(false);
    }
  }

  async function confirmRemovePrivate(target: AssistantSession, item: AssistantPrivateData) {
    setDialogBusy(true);
    setDialogError(null);
    try {
      const saved = await assistantApi.putPrivateData(target.id, withoutItem(target, item));
      if (saved.id === sessionId) setSession(saved);
      setDialog(null);
      void loadPrivacy(saved.id);
    } catch (error) {
      setDialogError(privateDataError(t, error));
    } finally {
      setDialogBusy(false);
    }
  }

  const oneColumn = !lg;
  // Below md one pane shows at a time; moving between them moves focus to its heading (H5).
  const pane: Pane = sessionId === null ? 'sessions' : privacyOpen ? 'privacy' : 'chat';
  const shownPane = useRef<Pane>(pane);
  const pendingFocus = useRef<Pane | null>(null);
  useEffect(() => {
    if (!oneColumn) {
      shownPane.current = pane;
      return;
    }
    if (shownPane.current !== pane) {
      shownPane.current = pane;
      pendingFocus.current = pane;
    }
    const target = pendingFocus.current;
    if (target === null) return;
    // The chat's heading is its session's name: wait for the session before moving focus.
    if (target === 'chat' && (session === null || session.id !== sessionId)) return;
    const heading = {
      sessions: sessionsHeading,
      chat: chatHeading,
      privacy: privacyHeading,
    }[target];
    const node = document.getElementById(heading);
    if (node !== null) {
      node.focus();
      pendingFocus.current = null;
    }
  });

  const paneSx = {
    minWidth: 0,
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    bgcolor: tokens.bg,
  } as const;

  /**
   * Where a cross-session hit is (item 13): its label, and whether its lines are other members'.
   * Only sessions the person lists show; a private room only to a subscriber (item 6a), as "My
   * private room · {shared session}". Null hides the hit.
   */
  const searchPlace = (hit: AssistantSearchHit): SearchPlace | null => {
    const where =
      hit.sessionId === null
        ? undefined
        : (known.get(hit.sessionId) ?? (support?.id === hit.sessionId ? support : undefined));
    if (where === undefined) return null;
    if (where.visibility === 'BREAKOUT') {
      if (me.tier !== 'SUBSCRIBER') return null;
      return {
        label: t('assistant.search.privateRoom', {
          parentName: where.parentName ?? hit.sessionName ?? where.name,
        }),
        shared: false,
      };
    }
    if (where.kind === 'SUPPORT') return { label: t('support.label'), shared: false };
    return { label: hit.sessionName ?? where.name, shared: where.visibility === 'SHARED' };
  };

  const sessionsPane = (
    <Box
      component="section"
      aria-labelledby={sessionsHeading}
      sx={{ ...paneSx, ...(oneColumn ? {} : { borderInlineEnd: `1px solid ${tokens.line}` }) }}
      data-pane="sessions"
    >
      {listError !== null && active === null ? (
        <Box sx={{ p: 2 }}>
          {/* The pane keeps its name while the list can't load. */}
          <Box
            component="h2"
            id={sessionsHeading}
            tabIndex={-1}
            sx={{
              m: 0,
              mb: 2,
              fontFamily: (current) => current.typography.h4.fontFamily,
              fontWeight: 400,
              fontSize: 24,
            }}
          >
            {t('nav.assistant')}
          </Box>
          <ErrorState
            error={listError}
            what={t('assistant.loadSessionsError')}
            onRetry={() => void loadLists()}
          />
        </Box>
      ) : (
        <SessionList
          headingId={sessionsHeading}
          active={active}
          archived={archived}
          showArchived={showArchived}
          onShowArchived={setShowArchived}
          selectedId={sessionId}
          support={support}
          newPrivate={newPrivate}
          newShared={newShared}
          onAction={(target, action) => void act(target, action)}
          searchPlace={searchPlace}
        />
      )}
    </Box>
  );

  const isSupport = session !== null && session.id === sessionId && session.kind === 'SUPPORT';
  const sideWidth = isSupport ? SUPPORT_WIDTH : PRIVACY_WIDTH;
  const closeSide = (close: boolean) =>
    close ? (
      <IconButton
        aria-label={isSupport ? t('support.closePanel') : t('assistant.closePrivacy')}
        onClick={() => setPrivacyOpen(false)}
      >
        <CloseIcon />
      </IconButton>
    ) : undefined;

  const privacyContent = (close: boolean) =>
    isSupport && !supportPrivacy ? (
      <SupportPanel
        headingId={privacyHeading}
        supportSessionId={support?.id ?? sessionId}
        request={supportRequest}
        ticketsVersion={ticketsVersion}
        onShowPrivacy={() => setSupportPrivacy(true)}
        close={closeSide(close)}
      />
    ) : (
      <>
        {isSupport && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Button
              size="small"
              startIcon={<PreviousIcon />}
              onClick={() => setSupportPrivacy(false)}
              data-back-to-support=""
            >
              {t('support.backToPanel')}
            </Button>
          </Box>
        )}
        <PrivacyPanel
          headingId={privacyHeading}
          privacy={privacy}
          loadError={privacyError}
          onRetry={() => sessionId !== null && void loadPrivacy(sessionId)}
          registry={options?.categories ?? null}
          shared={session?.visibility === 'SHARED'}
          agentTools={!isSupport}
          close={closeSide(close)}
        />
      </>
    );

  const backToList = oneColumn ? (
    <Button
      size="small"
      startIcon={<PreviousIcon />}
      onClick={() => void navigate('/me/assistant')}
    >
      {t('assistant.backToSessions')}
    </Button>
  ) : undefined;
  // The panel is a column at xl; elsewhere the header's button opens it.
  // An icon button named by its tooltip, on the header's one row (TMMAPP-0156 item 1).
  const panelLabel = isSupport ? t('support.openPanel') : t('assistant.openPrivacy');
  const panelButton = xl ? undefined : (
    <Tooltip title={panelLabel}>
      <IconButton size="small" aria-label={panelLabel} onClick={() => setPrivacyOpen(true)}>
        {isSupport ? <HelpIcon /> : <PrivacyIcon />}
      </IconButton>
    </Tooltip>
  );
  const roomParent: RoomParent | null =
    parentView !== null && session !== null && parentView.id === session.parentSessionId
      ? roomParentOf(parentView, otherMemberNames(t, parentView))
      : null;

  let chatPane = null;
  if (sessionId !== null) {
    chatPane = (
      <Box component="section" aria-labelledby={chatHeading} sx={paneSx} data-pane="chat">
        {session !== null && session.id === sessionId ? (
          <ChatPanel
            key={session.id}
            headingId={chatHeading}
            session={session}
            options={options}
            wallet={wallet}
            onWallet={setWallet}
            aiAvailable={me.user.aiEnabled}
            onTurnEnd={onTurnEnd}
            onArrived={onArrived}
            onSessionStale={() => void loadSession(session.id)}
            onEditScope={() => void editScope(session)}
            onRestore={() => void act(session, 'restore')}
            onAllowScope={onAllowScope}
            onAddPrivateData={addMine}
            onRemovePrivateData={(item) => openDialog({ kind: 'removePrivate', session, item })}
            onRead={() => onRead(session.id)}
            onOpenArticle={
              session.kind === 'SUPPORT'
                ? (id) => openInSupport({ kind: 'article', id })
                : undefined
            }
            onTicketFiled={() => setTicketsVersion((version) => version + 1)}
            onOpenTicket={(number) => openInSupport({ kind: 'ticket', number })}
            onAgentTools={
              session.kind === 'SUPPORT'
                ? undefined
                : () => openDialog({ kind: 'agentTools', session })
            }
            onOpenRoom={() => void openRoom(session)}
            openingRoom={openingRoom}
            onOpenShared={() => openShared(session)}
            onShareBack={(note) => shareBack(session, note)}
            parent={roomParent}
            parentSettled={parentSettled}
            parentFailure={parentFailure}
            jumpTo={jump !== null && jump.sessionId === session.id ? jump : null}
            // Below lg the chat stays mounted, hidden, while Privacy or Help and tickets shows (E2).
            visible={!(oneColumn && pane === 'privacy')}
            onDelete={() => openDialog({ kind: 'delete', session })}
            filesSent={privacy?.sent.some((call) => call.attachments.length > 0) ?? false}
            initialText={
              composerDraft !== null && composerDraft.sessionId === session.id
                ? composerDraft.text
                : undefined
            }
            back={backToList}
            panelButton={panelButton}
          />
        ) : (
          <Box sx={{ p: 2 }}>
            {backToList}
            <Box
              component="h2"
              id={chatHeading}
              tabIndex={-1}
              sx={{ m: 0, fontSize: 16, fontWeight: 600 }}
            >
              {t('assistant.region.chat')}
            </Box>
            {sessionError !== null ? (
              <Box sx={{ mt: 2 }}>
                <ErrorState
                  error={sessionError}
                  what={t('assistant.loadSessionError')}
                  onRetry={() => void loadSession(sessionId)}
                />
              </Box>
            ) : (
              <Box sx={{ mt: 2, fontSize: 14, color: tokens.text2 }}>{t('common.loading')}</Box>
            )}
          </Box>
        )}
      </Box>
    );
  } else if (!oneColumn) {
    chatPane = (
      <Box component="section" aria-labelledby={chatHeading} sx={paneSx} data-pane="chat">
        <Box sx={{ p: 3.5 }}>
          <Box component="h2" id={chatHeading} sx={{ m: 0, fontSize: 16, fontWeight: 600 }}>
            {t('assistant.region.chat')}
          </Box>
          <Box component="p" sx={{ fontSize: 14, color: tokens.text2 }} data-no-session="">
            {t('assistant.noSession')}
          </Box>
          <Box component="p" sx={{ fontSize: 12, color: tokens.text2 }}>
            {t('assistant.disclaimer')}
          </Box>
        </Box>
      </Box>
    );
  }

  const privacyPane =
    sessionId !== null ? (
      <Box
        ref={setSideScroll}
        component="section"
        aria-labelledby={privacyHeading}
        sx={{
          ...paneSx,
          overflow: 'auto',
          bgcolor: tokens.surface,
          ...(oneColumn ? {} : { borderInlineStart: `1px solid ${tokens.line}` }),
        }}
        data-pane="privacy"
      >
        <LazyRootContext.Provider value={sideScroll}>
          {privacyContent(!xl)}
        </LazyRootContext.Provider>
      </Box>
    ) : null;

  // The workspace fills the page below the top bar, edge to edge (it takes back the main column's
  // padding); each pane scrolls on its own.
  const drawer = (
    <Drawer
      anchor="right"
      open={privacyOpen && privacyPane !== null}
      onClose={() => setPrivacyOpen(false)}
      slotProps={{ paper: { ref: setSideScroll, sx: { width: sideWidth, maxWidth: '100%' } } }}
    >
      <Box component="section" aria-labelledby={privacyHeading} data-pane="privacy">
        <LazyRootContext.Provider value={sideScroll}>
          {privacyContent(true)}
        </LazyRootContext.Provider>
      </Box>
    </Drawer>
  );
  let workspace;
  if (xl) {
    workspace = (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${LIST_WIDTH}px minmax(0, 1fr) ${sideWidth}px`,
          height: '100%',
        }}
      >
        {sessionsPane}
        {chatPane}
        {privacyPane ?? (
          <Box
            component="section"
            aria-labelledby={privacyHeading}
            sx={{
              ...paneSx,
              bgcolor: tokens.surface,
              borderInlineStart: `1px solid ${tokens.line}`,
            }}
            data-pane="privacy"
          >
            <Box sx={{ p: 2 }}>
              <Box component="h2" id={privacyHeading} sx={{ m: 0, fontSize: 16, fontWeight: 600 }}>
                {t('assistant.region.privacy')}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  } else if (lg) {
    workspace = (
      <>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `${LIST_WIDTH}px minmax(0, 1fr)`,
            height: '100%',
          }}
        >
          {sessionsPane}
          {chatPane}
        </Box>
        {drawer}
      </>
    );
  } else {
    // One pane at a time. The chat stays mounted, hidden, while Privacy (or Help and tickets)
    // shows, so its draft, its text and its turn survive the swap (M2).
    const side = pane === 'privacy' && privacyPane !== null;
    workspace = (
      <Box sx={{ height: '100%' }}>
        {pane === 'sessions' ? (
          sessionsPane
        ) : (
          <>
            {chatPane !== null && (
              <Box sx={{ height: '100%', display: side ? 'none' : 'block' }} data-chat-host="">
                {chatPane}
              </Box>
            )}
            {side && privacyPane}
          </>
        )}
      </Box>
    );
  }

  const shared = (target: AssistantSession) => target.visibility === 'SHARED';
  const room = (target: AssistantSession) => target.visibility === 'BREAKOUT';
  const otherMembers =
    session === null
      ? null
      : session.members
          .filter((member) => !member.mine)
          .map((member) => personName(member.name, t));

  return (
    <>
      <Box
        data-assistant-workspace=""
        sx={{
          position: 'relative',
          // The main column's padding (32, 16 at xs) is the workspace's to fill (contract §11).
          mx: { xs: -2, sm: -4 },
          mb: { xs: -2, sm: -4 },
          '&:first-of-type': { mt: { xs: -2, sm: -4 } },
          // Flex fill (TMMAPP-0154 §A 4): the workspace takes what the shell's column leaves under
          // the top bar, whatever the bar's height, never `100dvh - BAR_HEIGHT`. Its content sits in
          // an absolute box, so the panels' `height: 100%` has a definite height to resolve against.
          flex: '1 1 0',
          minHeight: 480,
          overflow: 'hidden',
          bgcolor: tokens.bg,
        }}
      >
        {/* The page's one h1, for route focus and the outline; the list's title is what shows. */}
        <Box sx={visuallyHidden}>
          <PageHeader title={title} subtitle={t('assistant.subtitle')} />
        </Box>
        <Box data-assistant-fill="" sx={{ position: 'absolute', inset: 0 }}>
          {workspace}
        </Box>
      </Box>
      {dialog?.kind === 'create' && options !== null && (
        <SessionScopeDialog
          options={options}
          initialVisibility={dialog.visibility}
          onClose={() => setDialog(null)}
          onSaved={(saved) => {
            void loadLists();
            void navigate(`/me/assistant/${saved.id}`);
          }}
        />
      )}
      {dialog?.kind === 'create' && options === null && (
        <ConfirmDialog
          title={t('assistant.dialog.createTitle')}
          consequence={t('assistant.loadOptionsError')}
          confirmLabel={t('common.retry')}
          hideConfirm
          onConfirm={() => undefined}
          onCancel={() => {
            setDialog(null);
            void loadOptions();
          }}
        />
      )}
      {dialog?.kind === 'scope' && options !== null && (
        <SessionScopeDialog
          options={options}
          session={dialog.session}
          parent={dialog.parent}
          onClose={() => setDialog(null)}
          onSaved={(saved) => {
            setSession(saved);
            void loadPrivacy(saved.id);
            void loadLists();
          }}
        />
      )}
      {dialog?.kind === 'rename' && (
        <RenameSessionDialog
          session={dialog.session}
          onClose={() => setDialog(null)}
          onRenamed={(saved) => {
            if (saved.id === sessionId) setSession(saved);
            notify(t('assistant.renamed'));
            void loadLists();
          }}
        />
      )}
      {dialog?.kind === 'archive' && (
        <ConfirmDialog
          title={t('assistant.shared.archiveTitle')}
          consequence={t('assistant.shared.archiveConsequence')}
          confirmLabel={t('assistant.shared.archiveConfirm')}
          busy={dialogBusy}
          error={dialogError}
          onConfirm={() => void confirmArchive(dialog.session)}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'delete' && (
        <ConfirmDialog
          title={
            shared(dialog.session)
              ? t('assistant.shared.deleteTitle')
              : room(dialog.session)
                ? t('assistant.breakout.deleteTitle')
                : t('assistant.deleteTitle', { name: dialog.session.name })
          }
          consequence={
            shared(dialog.session)
              ? t('assistant.shared.deleteConsequence')
              : room(dialog.session)
                ? t('assistant.breakout.deleteConsequence')
                : t('assistant.deleteConsequence')
          }
          confirmLabel={
            shared(dialog.session)
              ? t('assistant.shared.deleteConfirm')
              : room(dialog.session)
                ? t('assistant.breakout.deleteConfirm')
                : t('assistant.deleteConfirm')
          }
          destructive
          busy={dialogBusy}
          error={dialogError}
          onConfirm={() => void confirmDelete(dialog.session)}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'privateData' && options !== null && (
        <PrivateDataDialog
          session={dialog.session}
          options={options}
          otherMembers={otherMembers}
          privateRoom={room(dialog.session)}
          onClose={() => setDialog(null)}
          onSaved={(saved) => {
            if (saved.id === sessionId) setSession(saved);
            void loadPrivacy(saved.id);
          }}
        />
      )}
      {dialog?.kind === 'agentTools' && (
        <SessionAgentToolsDialog
          session={session !== null && session.id === dialog.session.id ? session : dialog.session}
          onChanged={() => void loadSession(dialog.session.id)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'removePrivate' && (
        <ConfirmDialog
          title={t('assistant.shared.removeTitle', {
            item: privateDataItem(t, dialog.item, options?.categories),
          })}
          consequence={t('assistant.shared.removeConsequence')}
          confirmLabel={t('assistant.shared.removeConfirm')}
          busy={dialogBusy}
          error={dialogError}
          onConfirm={() => void confirmRemovePrivate(dialog.session, dialog.item)}
          onCancel={() => setDialog(null)}
        />
      )}
    </>
  );
}
